import { useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useSimStore } from './store/simStore'
import { LobbyScreen } from './components/LobbyScreen'
import { HUD } from './components/HUD'
import { KpiDashboard } from './components/KpiDashboard'
import { GanttPanel } from './components/GanttPanel'
import { InspectorPanel } from './components/InspectorPanel'
import { NotificationToast } from './components/NotificationToast'
import { PauseOverlay } from './components/PauseOverlay'
import { ResultsScreen } from './components/ResultsScreen'
import { FactorySVG } from './canvas/FactorySVG'
import { useSimPoll } from './hooks/useSimPoll'
import { useKeyboard } from './hooks/useKeyboard'

export default function App() {
  const {
    appState, setAppState, runId,
    instanceConfig, snapshot, resetRun,
  } = useSimStore()

  // Phase 31B: poll REST snapshot instead of SSE stream
  useSimPoll(runId)

  // Global keyboard shortcuts
  useKeyboard(() => {})

  // ── LOADING: transition to RUNNING after boot animation ───────────────────
  useEffect(() => {
    if (appState === 'LOADING' && instanceConfig) {
      const t = setTimeout(() => setAppState('RUNNING'), 1800)
      return () => clearTimeout(t)
    }
  }, [appState, instanceConfig])

  async function handlePause() {
    if (!runId) return
    if (appState === 'RUNNING') {
      setAppState('PAUSED')
      try {
        const res = await fetch(`/api/sim/${runId}/pause`, { method: 'PATCH' })
        if (!res.ok) setAppState('RUNNING')
      } catch { setAppState('RUNNING') }
    } else if (appState === 'PAUSED') {
      setAppState('RUNNING')
      try {
        const res = await fetch(`/api/sim/${runId}/resume`, { method: 'PATCH' })
        if (!res.ok) setAppState('PAUSED')
      } catch { setAppState('PAUSED') }
    }
  }

  function handleStop() {
    resetRun()
    setAppState('LOBBY')
  }

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-[#0d1117]">
      <AnimatePresence mode="wait">

        {appState === 'LOBBY' && (
          <motion.div key="lobby" className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <LobbyScreen />
          </motion.div>
        )}

        {appState === 'LOADING' && instanceConfig && (
          <motion.div
            key="loading"
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#0d1117]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="text-5xl mb-4"
            >
              ⚙
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-teal-400 font-mono font-bold text-lg tracking-widest"
            >
              BOOTING FACTORY…
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-slate-500 text-sm mt-2"
            >
              {instanceConfig.name} · {instanceConfig.n_machines} machines · {instanceConfig.n_orders} orders
            </motion.p>
          </motion.div>
        )}

        {(appState === 'RUNNING' || appState === 'PAUSED') && instanceConfig && (
          <motion.div
            key="sim"
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: appState === 'PAUSED' ? 0.5 : 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Phase 31C: SVG factory floor — safe area (avoid HUD/KPI/Gantt panels) */}
            <div className="absolute top-10 left-0 right-64 bottom-52 flex items-center justify-center">
              {snapshot
                ? <FactorySVG snapshot={snapshot} />
                : (
                  <p className="text-slate-500 font-mono text-sm animate-pulse">
                    Waiting for first snapshot…
                  </p>
                )
              }
            </div>

            {/* HUD overlay */}
            <HUD onPause={handlePause} onStop={handleStop} />

            {/* Right: KPI dashboard */}
            <KpiDashboard />

            {/* Bottom: Gantt */}
            <GanttPanel />

            {/* Inspector slide-in */}
            <InspectorPanel />

            {/* Pause overlay */}
            <AnimatePresence>
              {appState === 'PAUSED' && (
                <motion.div key="pause-overlay" className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <PauseOverlay onResume={handlePause} onStop={handleStop} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {appState === 'RESULTS' && (
          <motion.div key="results" className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ResultsScreen />
          </motion.div>
        )}

        {appState === 'ERROR' && (
          <motion.div key="error" className="absolute inset-0 flex flex-col items-center justify-center bg-[#0d1117]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="text-rose-400 text-4xl mb-4">⚠</div>
            <div className="text-rose-400 font-bold text-xl">Connection Error</div>
            <p className="text-slate-400 text-sm mt-2">The simulation stream was lost.</p>
            <button
              onClick={() => { resetRun(); setAppState('LOBBY') }}
              className="mt-6 px-6 py-2 bg-[#21262d] hover:bg-[#30363d] rounded-xl"
            >
              Back to Lobby
            </button>
          </motion.div>
        )}

      </AnimatePresence>

      {/* Notifications always on top */}
      <NotificationToast />
    </div>
  )
}
